import { CalendarTaskSync, EntityLinks } from '@griply/types';
import React, { useMemo } from 'react';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { TaskActionGoal } from 'shared/types/task-action';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { TaskEventSyncBadge } from './task-event-sync-badge';

export type GoalEventProps = {
  task: Task;
  goals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  onClick?: (id: ID) => void;
  numberOfLines: number;
  timeText?: string;
  completed?: boolean;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

export const TaskGoalEvent: React.FC<GoalEventProps> = ({
  task,
  goals,
  lifeAreas,
  links,
  onClick,
  numberOfLines,
  timeText,
  completed,
  onInteractionComplete,
  calendarSync,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const _onClick = () => onClick?.(task.id);

  const goal = useMemo(
    () =>
      goals.find(({ id }) => (task.action as TaskActionGoal)?.meta?.id === id),
    [goals, task.action],
  );

  const taskCounts = useTaskCounts();

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(
            getGoalCompletion(goal, {
              goals,
              goalProgressCounts: taskCounts?.byGoalProgress,
            }),
          )
        : undefined,
    [goal, goals, taskCounts?.byGoalProgress],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas, links }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas, links],
  );

  if (!goal) {
    return null;
  }

  return (
    <>
      <EventCard
        title={goal.name}
        color={color}
        timeText={timeText}
        completed={completed}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        trailing={
          <TaskEventSyncBadge
            task={task}
            calendarSync={calendarSync}
            color={color}
          />
        }
        leading={
          <Styled.LeadingSlot $color={color} $completed={completed}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <TaskCardContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
          showCalendarSync
        />
      )}
    </>
  );
};
